import { EMPTY_STATE_IMAGES } from '@shared/constants';
import { DeleteIcon } from '@sketchnote/icons';
import { Avatar } from '@sketchnote/shared-components';
import { LoaderXs } from '@sketchnote/shared-components';
import { resetEditorValue } from '@sketchnote/slate-plugins';
import { ELEMENT_DEFAULT, TDescendant, usePlateEditorRef } from '@udecode/plate';
import { formatDistance, parseISO } from 'date-fns';
import flatten from 'lodash/flatten';
import { useRouter } from 'next/router';
import * as React from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Node } from 'slate';
import { API } from 'src/api';
import { BlockEditor } from 'src/components/BlockEditor';
import { PageErrorBoundary } from 'src/components/error-boundary/page-error-boundary';
import { EditorProvider } from 'src/features/collaborative-editor/components/EditorProvider';
import { EmptyStateDashboard } from 'src/features/dashboard/components/EmptyState';
import { useAuth } from 'src/shared/hooks/use-auth';
import { v4 as uuid } from 'uuid';

import useChatStore from '../store/useChatStore';
const { useEffect, useMemo, useState, useCallback, useRef } = React;

const ChatViewBase = ({ id, isPublic = false }: { id: string; isPublic?: boolean }) => {
  const {
    chat,
    updateChat,
    updateChatPage,
    liveblocks: { enterRoom, leaveRoom, isStorageLoading, room, connection },
  } = useChatStore();
  const editorChat = usePlateEditorRef(id)!;
  const wrapperRef = useRef<null | HTMLDivElement>(null);
  const initialValue = [{ type: 'p', children: [{ text: '' }] }];
  const [hoveredIndex, setHoveredIndex] = useState<string>('');
  const [currentChatData, setCurrentChatData] = useState<TDescendant[]>(initialValue);
  const userDetails = useAuth((s) => s.userDetails);
  const router = useRouter();
  const chatElementToScrollTo = document.getElementById(router?.query?.chatBlockId as string);
  useEffect(() => {
    enterRoom(id);
    return () => {
      leaveRoom(id);
      updateChatPage([]);
    };
  }, [enterRoom, leaveRoom, id]);

  const checkForEmptySlateObject = () => {
    return currentChatData?.filter(
      (item) =>
        (item.type === ELEMENT_DEFAULT && Node.string(item).length !== 0) ||
        item.type !== ELEMENT_DEFAULT
    );
  };

  const handleMentionData = (data: any) => {
    const mentionList: any = flatten(
      data.map((item) => {
        return item?.children?.filter((obj) => obj.type === 'mention');
      })
    );
    return mentionList.map((obj) => obj.data.value);
  };

  const getMention = (value): string => {
    if (typeof value === 'string') {
      return value;
    } else if (Array.isArray(value)) {
      return value
        .map((obj) => {
          return obj.children
            .map((child) => {
              if (child.type === 'mention') {
                return child.data.name;
              }
              return Node.string(child);
            })
            .join('');
        })
        .join('\n');
    }
    return '';
  };

  const handleSubmitMessage = async () => {
    const data = checkForEmptySlateObject();
    const currentChatId = uuid();
    if (data.length > 0) {
      const mentionList = handleMentionData(data);
      updateChat({
        member: {
          avatar: userDetails?.avatarUrl || '',
          email: userDetails?.email || '',
          firstname: userDetails?.memberInformation?.firstname || '',
          id: userDetails?.memberInformation?.memberId || '',
          lastname: userDetails?.memberInformation?.lastname || '',
        },
        id: currentChatId,
        timeStamp: JSON.stringify(new Date()),
        chatData: data,
      });
      if (mentionList.length > 0) {
        await API.post('chat', {
          file: router.query.slug as string,
          memberId: mentionList,
          chat: id,
          viewId: router.query.viewId as string,
          blockId: currentChatId,
          text: getMention(data),
        });
      }
    }
    resetEditorValue(initialValue, editorChat as any);
    setCurrentChatData(initialValue);
  };

  const handleOnEnter = (keyboardEvent: React.KeyboardEvent<HTMLDivElement>) => {
    if (keyboardEvent.code === 'Enter') {
      keyboardEvent.preventDefault();
      handleSubmitMessage();
    }
  };

  const chatBlock = useMemo(
    () => (
      <BlockEditor
        id={id}
        mode="chat"
        paddingBottomOnAddNewBlock="50px"
        onChange={(e) => {
          setCurrentChatData(e);
        }}
        isReadOnly={connection !== 'open'}
        onEnter={handleOnEnter}
        value={currentChatData}
        selectionToolbarProps={{
          hide: {
            changeBgColor: false,
            changeBlock: false,
            changeColor: false,
            comment: true,
          },
        }}
      />
    ),
    [id, currentChatData, connection]
  );

  const handleScrollToEndOfChatFeed = () => {
    const chatContainer = document.getElementById('chat-feed');
    const scrollHeight = chatContainer?.scrollHeight || 0;
    chatContainer?.scrollTo(0, scrollHeight + 500);
  };

  const handleDeleteMessage = (id: string) => {
    const chatData = chat.filter((item) => item.id !== id);
    updateChatPage(chatData);
  };

  useEffect(() => {
    if (!router.query.chatBlockId) handleScrollToEndOfChatFeed();
  }, [chat, isStorageLoading]);

  const handleScrollToChatBlock = () => {
    chatElementToScrollTo?.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' });

    setTimeout(() => {
      const { chatBlockId, ...routerQuery } = router.query;
      router.replace({
        query: { ...routerQuery },
      });
    }, 5000);
  };

  useEffect(() => {
    if (!isStorageLoading && router.query.chatBlockId && chatElementToScrollTo) {
      handleScrollToChatBlock();
    }
  }, [isStorageLoading, chatElementToScrollTo]);

  const renderChatMessage = useCallback(
    (index, item) => {
      return (
        <DndProvider backend={HTML5Backend}>
          <EditorProvider id={item.id}>
            <BlockEditor
              id={item.id}
              isReadOnly={true}
              paddingBottomOnAddNewBlock="3px"
              value={item.chatData}
            />
          </EditorProvider>
        </DndProvider>
      );
    },
    [chat]
  );

  return isStorageLoading ? (
    <LoaderXs className="mx-auto text-xss mt-200" />
  ) : (
    <>
      {connection !== 'open' && (
        <div
          style={{ left: '25%', top: '10%' }}
          className=" bg-white sticky w-575 text-center z-20 shadow-md border py-20 text-sm text-gray-600 rounded-sm"
        >
          You are currently offine. Please refresh to sync the file.
        </div>
      )}
      <PageErrorBoundary wrapperClass="mt-32">
        <div
          className={`overflow-hidden  flex flex-col ${connection !== 'open' && 'opacity-50'} ${
            isPublic ? '' : 'pt-64'
          }`}
          style={{ height: 'calc(100vh - 50px' }}
        >
          {chat.length > 0 ? (
            <div
              className="flex-grow  overflow-scroll no-scrollbar"
              id="chat-feed"
              ref={wrapperRef}
            >
              {chat.map((item, index) => {
                return (
                  <PageErrorBoundary errorPage="chat" key={index}>
                    <div
                      className="mb-5 flex justify-between px-24 py-14"
                      onMouseEnter={() => setHoveredIndex(item.id)}
                      onMouseLeave={() => setHoveredIndex('')}
                      id={item.id}
                      style={{ backgroundColor: `${index % 2 === 0 ? '#FFFFFF' : '#F6F7F9'}` }}
                    >
                      <div className="w-full">
                        <div className="flex justify-between items-center">
                          <div className="flex mb-2 items-center">
                            <Avatar
                              name={item?.member?.firstname || ''}
                              size="sm1"
                              type="photo"
                              src={item.member?.avatar}
                              wrapperClassName="border"
                            />
                            <div className="flex-row">
                              <label className="text-sm font-medium text-gray-999 ml-12">
                                {item?.member?.firstname}
                              </label>

                              <span className="text-xss text-gray-600 ml-6">
                                {item?.timeStamp &&
                                  formatDistance(parseISO(JSON.parse(item.timeStamp)), new Date(), {
                                    addSuffix: true,
                                  })}
                              </span>
                            </div>
                          </div>
                          {hoveredIndex === item.id &&
                            userDetails?.memberInformation?.memberId === item?.member?.id && (
                              <button onClick={() => handleDeleteMessage(item.id)}>
                                <DeleteIcon className="text-gray-500 text-sm" />
                              </button>
                            )}
                        </div>
                        <div className="text-gray-999 text-sm font-regular">
                          {renderChatMessage(index, item)}
                        </div>
                      </div>
                    </div>
                  </PageErrorBoundary>
                );
              })}
            </div>
          ) : !isPublic ? (
            <div className="px-24 flex-grow text-gray-500 overflow-scroll no-scrollbar flex flex-col justify-end py-16">
              <div>
                This is the beginning of your new chat. Invite your colleagues and tag them with the
                @ command to discuss projects and tasks.
                <span className="block text-blue-500 font-medium">
                  Chat with your teammates in real-time 👋
                </span>
              </div>
            </div>
          ) : (
            <EmptyStateDashboard
              contents={['No chat found!']}
              src={EMPTY_STATE_IMAGES.lessonCompleted}
            />
          )}

          {!isPublic && (
            <div
              style={{ boxShadow: '0px -2px 4px rgba(19, 23, 32, 0.06)' }}
              className="align-bottom  rounded-t-sm pt-4 flex flex-col py-8"
            >
              <div className="flex-grow max-h-256 px-64 overflow-scroll">
                {' '}
                <DndProvider backend={HTML5Backend}>{chatBlock}</DndProvider>
              </div>
              <div className="flex  justify-end pl-24 pr-72 py-4">
                <button
                  onClick={handleSubmitMessage}
                  className="bg-blue-500 text-white rounded-sm h-36 text-sm text-center px-16"
                >
                  Send
                </button>
              </div>
            </div>
          )}
        </div>
      </PageErrorBoundary>
    </>
  );
};

export const ChatView = ({ id, isPublic }: { id: string; isPublic?: boolean }) => (
  <EditorProvider id={id} mode="chat">
    <ChatViewBase id={id} isPublic={isPublic} />
  </EditorProvider>
);
